<template>
  <div class="admin-page">
    <!-- Header -->
    <header class="admin-header">
      <div class="header-left">
        <div class="logo">
          <span class="logo-icon">🛡️</span>
        </div>
        <div class="header-titles">
          <h1 class="app-title">Administration</h1>
          <router-link to="/" class="breadcrumb">← Back to Hub</router-link>
        </div>
      </div>
      <div class="header-right">
        <span class="username">{{ username }}</span>
        <button @click="logout" class="logout-btn">Logout</button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="admin-content">
      <!-- Statistics Cards -->
      <div class="stats-section">
        <h2 class="section-title">Platform Statistics</h2>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon">👥</div>
            <div class="stat-info">
              <span class="stat-value">{{ stats.totalUsers }}</span>
              <span class="stat-label">Total Users</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">✅</div>
            <div class="stat-info">
              <span class="stat-value">{{ stats.activeUsers }}</span>
              <span class="stat-label">Active Users</span>
            </div>
          </div>
          <div class="stat-card pending">
            <div class="stat-icon">⏳</div>
            <div class="stat-info">
              <span class="stat-value">{{ stats.pendingUsers }}</span>
              <span class="stat-label">Pending Approval</span>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">🔗</div>
            <div class="stat-info">
              <span class="stat-value">{{ stats.totalPmsConfigs }}</span>
              <span class="stat-label">PMS Configured</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pending Users Section -->
      <div class="users-section">
        <div class="section-header">
          <h2 class="section-title">Users Pending Approval</h2>
          <span class="count-badge" v-if="pendingUsers.length > 0">{{ pendingUsers.length }}</span>
        </div>

        <!-- Pending Users List -->
        <div v-if="pendingUsers.length > 0" class="users-list">
          <div v-for="user in pendingUsers" :key="user.id" class="user-card">
            <div class="user-avatar">
              {{ getInitials(user.username || user.email) }}
            </div>
            <div class="user-info">
              <h4 class="user-name">{{ user.username }}</h4>
              <p class="user-email">{{ user.email }}</p>
              <p class="user-date">Registered: {{ formatDate(user.created_at) }}</p>
            </div>
            <div class="user-actions">
              <button
                @click="approveUser(user.id)"
                class="approve-btn"
                :disabled="processingUser === user.id"
              >
                <span v-if="processingUser === user.id" class="loading-spinner"></span>
                <span v-else>Approve</span>
              </button>
              <button
                @click="rejectUser(user.id)"
                class="reject-btn"
                :disabled="processingUser === user.id"
              >
                Reject
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <div class="empty-icon">✨</div>
          <h4>All Caught Up!</h4>
          <p>There are no users waiting for approval.</p>
        </div>
      </div>

      <!-- All Users Section -->
      <div class="users-section">
        <div class="section-header">
          <h2 class="section-title">All Users</h2>
          <span class="count-badge">{{ allUsers.length }}</span>
        </div>

        <!-- Users Table -->
        <div v-if="allUsers.length > 0" class="table-container">
          <table class="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Status</th>
                <th>Role</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in allUsers" :key="user.id">
                <td class="user-name-cell">{{ user.username }}</td>
                <td>{{ user.email }}</td>
                <td>
                  <span class="status-badge" :class="user.status">
                    {{ formatStatus(user.status) }}
                  </span>
                </td>
                <td>
                  <span v-if="user.is_super_admin" class="role-badge admin">Admin</span>
                  <span v-else class="role-badge user">User</span>
                </td>
                <td>{{ formatDate(user.created_at) }}</td>
                <td>
                  <button
                    v-if="!user.is_super_admin"
                    @click="toggleUserApproval(user.id, user.status)"
                    class="action-btn"
                    :class="(user.status === 'approved' || user.status === 'active') ? 'disapprove-btn' : 'approve-btn'"
                    :disabled="processingUser === user.id"
                  >
                    {{ (user.status === 'approved' || user.status === 'active') ? 'Disapprove' : 'Approve' }}
                  </button>
                  <span v-else class="no-action">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-state">
          <p>No users found.</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import SETTINGS from '@/config/settings.js';

export default {
  name: 'AdminView',
  data() {
    return {
      username: '',
      stats: {
        totalUsers: 0,
        activeUsers: 0,
        pendingUsers: 0,
        totalPmsConfigs: 0
      },
      pendingUsers: [],
      allUsers: [],
      processingUser: null
    };
  },

  methods: {
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/login');
    },

    getInitials(name) {
      if (!name) return '?';
      return name.substring(0, 2).toUpperCase();
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatStatus(status) {
      const statuses = {
        'pending': 'Pending',
        'active': 'Active',
        'approved': 'Approved',
        'inactive': 'Inactive',
        'blocked': 'Blocked'
      };
      return statuses[status] || status;
    },

    getApiUrl(endpoint) {
      return `${SETTINGS.API.baseUrl}${endpoint}`;
    },

    async fetchUserProfile() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        this.username = user.username || user.email || 'Admin';

        // Check if user is super admin
        if (!user.is_super_admin) {
          this.$router.push('/');
          return;
        }
      } catch (error) {
        console.error('Error loading user:', error);
        this.$router.push('/');
      }
    },

    async fetchPendingUsers() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const apiUrl = this.getApiUrl('/api/users/pending/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.pendingUsers = data || [];
          this.stats.pendingUsers = this.pendingUsers.length;
        }
      } catch (error) {
        console.error('Error fetching pending users:', error);
      }
    },

    async fetchAllUsers() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        // This endpoint may need to be created in the backend
        const apiUrl = this.getApiUrl('/api/users/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.allUsers = data.results || data || [];
          this.stats.totalUsers = this.allUsers.length;
          this.stats.activeUsers = this.allUsers.filter(u => u.status === 'active').length;
        }
      } catch (error) {
        console.error('Error fetching all users:', error);
      }
    },

    async fetchStats() {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Fetch PMS configs count
      try {
        const apiUrl = this.getApiUrl('/api/users/pms-configs/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.stats.totalPmsConfigs = (data.results || data || []).length;
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    },

    async approveUser(userId) {
      this.processingUser = userId;
      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl(`/api/users/approve/${userId}/`);
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Remove user from pending list
          this.pendingUsers = this.pendingUsers.filter(u => u.id !== userId);
          this.stats.pendingUsers = this.pendingUsers.length;

          // Refresh all users and update stats
          await this.fetchAllUsers();
          this.updateStats();
        } else {
          const data = await response.json();
          alert(data.message || 'Failed to approve user');
        }
      } catch (error) {
        console.error('Error approving user:', error);
        alert('Failed to approve user. Please try again.');
      } finally {
        this.processingUser = null;
      }
    },

    updateStats() {
      this.stats.totalUsers = this.allUsers.length;
      this.stats.activeUsers = this.allUsers.filter(u => u.status === 'active' || u.status === 'approved').length;
      this.stats.pendingUsers = this.pendingUsers.length;
    },

    async toggleUserApproval(userId, currentStatus) {
      const isApproved = currentStatus === 'approved' || currentStatus === 'active';
      const action = isApproved ? 'disapprove' : 'approve';

      if (!confirm(`Are you sure you want to ${action} this user?`)) {
        return;
      }

      this.processingUser = userId;
      const token = localStorage.getItem('token');

      try {
        const endpoint = isApproved ? 'disapprove' : 'approve';
        const apiUrl = this.getApiUrl(`/api/users/${endpoint}/${userId}/`);
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Update user in list
          const userIndex = this.allUsers.findIndex(u => u.id === userId);
          if (userIndex !== -1) {
            this.allUsers[userIndex].status = isApproved ? 'pending' : 'approved';
          }
          this.updateStats();
        } else {
          const data = await response.json();
          alert(data.message || `Failed to ${action} user`);
        }
      } catch (error) {
        console.error(`Error ${action}ing user:`, error);
        alert(`Failed to ${action} user. Please try again.`);
      } finally {
        this.processingUser = null;
      }
    },

    async rejectUser(userId) {
      if (!confirm('Are you sure you want to reject this user? This action cannot be undone.')) {
        return;
      }

      this.processingUser = userId;
      const token = localStorage.getItem('token');

      try {
        // This endpoint may need to be created in the backend
        const apiUrl = this.getApiUrl(`/api/users/reject/${userId}/`);
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Remove user from pending list
          this.pendingUsers = this.pendingUsers.filter(u => u.id !== userId);
          this.stats.pendingUsers = this.pendingUsers.length;
        } else {
          const data = await response.json();
          alert(data.message || 'Failed to reject user');
        }
      } catch (error) {
        console.error('Error rejecting user:', error);
        alert('Failed to reject user. Please try again.');
      } finally {
        this.processingUser = null;
      }
    }
  },

  async mounted() {
    await this.fetchUserProfile();
    await Promise.all([
      this.fetchPendingUsers(),
      this.fetchAllUsers(),
      this.fetchStats()
    ]);
  }
};
</script>

<style scoped>
.admin-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* Header */
.admin-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.logo-icon {
  font-size: 24px;
}

.header-titles {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.app-title {
  color: white;
  font-size: 22px;
  font-weight: 700;
  margin: 0;
}

.breadcrumb {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.breadcrumb:hover {
  color: white;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.username {
  color: white;
  font-weight: 600;
  font-size: 15px;
}

.logout-btn {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: background 0.3s ease;
}

.logout-btn:hover {
  background: #c53030;
}

/* Main Content */
.admin-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Stats Section */
.stats-section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 20px 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-card {
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.stat-card.pending {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.stat-icon {
  font-size: 32px;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

/* Users Section */
.users-section {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-bottom: 30px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header .section-title {
  margin: 0;
}

.count-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
}

/* Users List */
.users-list {
  padding: 20px;
}

.user-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.user-card:last-child {
  margin-bottom: 0;
}

.user-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.15);
}

.user-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 16px;
  margin-right: 20px;
}

.user-info {
  flex: 1;
}

.user-name {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
}

.user-email {
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #64748b;
}

.user-date {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.user-actions {
  display: flex;
  gap: 10px;
}

.approve-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.approve-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
}

.approve-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.reject-btn {
  background: white;
  color: #dc2626;
  border: 2px solid #dc2626;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.3s ease;
}

.reject-btn:hover:not(:disabled) {
  background: #dc2626;
  color: white;
}

.reject-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Users Table */
.table-container {
  overflow-x: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table th {
  background: #f8fafc;
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
}

.users-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  color: #2d3748;
  font-size: 14px;
}

.users-table tbody tr:hover {
  background: #f7fafc;
}

.user-name-cell {
  font-weight: 600;
  color: #1a202c;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.active,
.status-badge.approved {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge.pending {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.inactive {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge.blocked {
  background: #fee2e2;
  color: #dc2626;
}

/* Action Buttons in Table */
.action-btn {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.approve-btn {
  background: #dcfce7;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.approve-btn:hover:not(:disabled) {
  background: #16a34a;
  color: white;
}

.disapprove-btn {
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.disapprove-btn:hover:not(:disabled) {
  background: #dc2626;
  color: white;
}

.no-action {
  color: #94a3b8;
}

.role-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.role-badge.admin {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.role-badge.user {
  background: #e2e8f0;
  color: #64748b;
}

/* Empty State */
.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.empty-state h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .admin-header {
    padding: 15px 20px;
    flex-direction: column;
    gap: 15px;
  }

  .header-left, .header-right {
    width: 100%;
    justify-content: center;
  }

  .app-title {
    font-size: 18px;
  }

  .admin-content {
    padding: 20px 15px;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .user-card {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }

  .user-avatar {
    margin-right: 0;
  }

  .user-actions {
    width: 100%;
    justify-content: center;
  }

  .users-table {
    font-size: 12px;
  }

  .users-table th,
  .users-table td {
    padding: 12px 8px;
  }
}
</style>
